import { IFilterSection } from "@faire/web-api/indigofair/data/IFilterSection";
import { useRefFrom } from "@faire/web/ui/hooks/useRefFrom";
import { useRef } from "react";

import {
  clearAllFilters as clearAllFiltersUtil,
  getSelectedFiltersCount,
} from "@faire/retailer/components/Filters/FilterUtils";
import { useFilterSectionSelectedChange } from "@faire/retailer/components/Filters/useFilterSectionSelectedChange";
import { getSettingAndAssignMillionLintOptimization } from "@faire/retailer/settings/getSettingAndAssignMillionLintOptimization";

/**
 * The interface which holds information needed to manage filters
 *
 * @export
 * @interface FilterState
 * @typedef {FilterState}
 */
export interface FilterState {
  /**
   * A copy of the filter section array received from the server
   * This should be deep cloned version of the data received from BE
   *
   * @type {IFilterSection[]}
   */
  filterSections: ReadonlyArray<IFilterSection>;
  /**
   * This is the indicator that a new filter is selected, It should be only
   * true if a new filter is selected and new fetch should happen
   *
   * @type {boolean}
   */
  isNewFilterSelected: boolean;
  /**
   * Indicator to set side bar open/close
   *
   * @type {boolean}
   */
  isSideBarVisible: boolean;
  /**
   * Indicator to set modal open/close
   *
   * @type {boolean}
   */
  isMobileModalOpen: boolean;
  /**
   * If this indicator set to true, page will scroll to top of results when ready
   *
   * @type {boolean}
   */
  shouldScrollToResults: boolean;
  /**
   * The number of filter sections which has at least one option selected
   *
   * @type {number}
   */
  activeFilterCount: number;
  /**
   * If this indicator set to true, we show a toggle to collapse/expand the filters
   * this property is optional and defaults to false
   *
   * @type {boolean}
   */
  shouldShowFilterCollapsePill?: boolean;
  /**
   * If set to true, we don't show the option count for each filter option
   * this property is optional and defaults to false
   *
   * @type {boolean}
   */
  hideOptionsResultCount?: boolean;
}

export namespace FilterState {
  export const build = (
    partial?: Partial<FilterState> | undefined
  ): FilterState => ({
    filterSections: partial?.filterSections ?? [],
    isNewFilterSelected: partial?.isNewFilterSelected ?? false,
    isSideBarVisible: partial?.isSideBarVisible ?? true,
    isMobileModalOpen: partial?.isMobileModalOpen ?? false,
    shouldScrollToResults: partial?.shouldScrollToResults ?? true,
    activeFilterCount:
      partial?.activeFilterCount ??
      (partial?.filterSections
        ? getSelectedFiltersCount(partial.filterSections)
        : -1),
    shouldShowFilterCollapsePill:
      partial?.shouldShowFilterCollapsePill ?? false,
    hideOptionsResultCount: partial?.hideOptionsResultCount ?? false,
  });
}

/**
 * The filter store interface
 *
 * @export
 * @interface FilterStore
 * @typedef {FilterStore}
 */
export interface FilterStore {
  /**
   * Internal get filter state
   *
   * @type {() => FilterState}
   */
  getState: () => FilterState;
  /**
   * Internal set filter state
   *
   * @type {(selector: FilterStoreSelector) => void}
   */
  setState: (selector: FilterStoreSelector) => void;
  /**
   * Internal subscribe to set with useSyncExternalStore
   *
   * @type {(
      callback: () => void,
      fields: readonly (keyof FilterState)[]
    ) => () => void}
   */
  subscribe: (
    callback: () => void,
    fields: readonly (keyof FilterState)[]
  ) => () => void;
  /**
   * Util function to clear all the filters
   *
   * @type {() => void}
   */
  clearAllFilters: () => void;
  /**
   * Set a callback function to run when a new filter being selected
   * Note: This function only call when isNewFilterSelected is true
   *
   * @deprecated this function is redundant if you use useCreateFilterStore
   */
  setOnFilterSelected?(callback: (state: FilterState) => void): void;
}

export type FilterStoreSelector =
  | Partial<FilterState>
  | ((prevState: FilterState) => Partial<FilterState>);

/**
 * Creates a filter store from a ref callback.
 *
 * @param initialState - The initial state of the filter store.
 * @param onFilterSelectedRef - A mutable ref object that holds a callback function to be called when a filter is selected.
 * @returns The filter store object with getState, setState, subscribe, and clearAllFilters methods.
 */
export const createFilterStore = (
  initialState: FilterState,
  onFilterSelectedRef: React.MutableRefObject<(state: FilterState) => void>
): FilterStore => {
  let currentState = initialState;
  const subscribers = new Map<keyof FilterState, Set<() => void>>();

  const getState = () => currentState;

  const setState = (selector: FilterStoreSelector) => {
    const partialStore =
      typeof selector === "function" ? selector(currentState) : selector;

    if (partialStore.filterSections !== undefined) {
      partialStore.activeFilterCount = getSelectedFiltersCount(
        partialStore.filterSections
      );
    }

    const shouldMemoMillionLint = getSettingAndAssignMillionLintOptimization();
    const changedFields = (
      Object.keys(partialStore) as (keyof FilterState)[]
    ).filter((field) => {
      if (shouldMemoMillionLint) {
        /**
         * Compare filterSections with shallow to correctly check if the contents of filterSections have changed.
         * We can't use === to compare filterSections because it's an array
         * The rest of the fields are primitives so we can use === to compare them.
         */
        if (field === "filterSections") {
          return (
            JSON.stringify(currentState[field]) !==
            JSON.stringify(partialStore[field])
          );
        }
        return currentState[field] !== partialStore[field];
      } else {
        return currentState[field] !== partialStore[field];
      }
    });

    if (changedFields.length === 0) {
      return;
    }

    const prevFilterSections = currentState.filterSections;
    currentState = Object.assign({}, currentState, partialStore);

    /**
     * Prevent re-renders due to filterSections reference changing but the value being the same.
     * Since we are creating a shallow copy of the filterSections, the reference will be different.
     * But the value is the same, so we don't want to re-render in that case.
     * i.e When isNewFilterSelected changes from true to false, we don't want to re-render everything depending on the filterSections.
     */
    if (shouldMemoMillionLint) {
      if (
        !partialStore.filterSections ||
        partialStore.filterSections === prevFilterSections ||
        !changedFields.includes("filterSections")
      ) {
        currentState.filterSections = prevFilterSections;
      }
    }

    if (currentState.isNewFilterSelected) {
      onFilterSelectedRef.current?.(currentState);
    }

    // Notify consumers that use the changed fields
    changedFields.forEach((field) => {
      const fieldSubscribers = subscribers.get(field);
      if (fieldSubscribers) {
        fieldSubscribers.forEach((callback) => callback());
      }
    });
  };

  const clearAllFilters = () => {
    setState((currentState) => {
      clearAllFiltersUtil(currentState.filterSections);
      return {
        ...currentState,
        isNewFilterSelected: true,
      };
    });
    useFilterSectionSelectedChange.getState().optionChanged()
  };

  const subscribe = (
    callback: () => void,
    fields: readonly (keyof FilterState)[]
  ) => {
    // Add the callback to the subscribers for each field
    fields.forEach((field) => {
      const fieldSubscribers = subscribers.get(field);
      if (fieldSubscribers) {
        fieldSubscribers.add(callback);
      } else {
        subscribers.set(field, new Set([callback]));
      }
    });

    // Return a function to unsubscribe from all fields
    return () => {
      fields.forEach((field) => {
        const fieldSubscribers = subscribers.get(field);
        if (fieldSubscribers) {
          const deleted = fieldSubscribers.delete(callback);
          if (!deleted) {
            // eslint-disable-next-line no-console
            console.warn(
              `useStore with fields ${fields} could not unsubscribe from field ${String(
                field
              )}`
            );
          }
        }
      });
    };
  };

  return {
    getState,
    setState,
    subscribe,
    clearAllFilters,
  };
};

export const useCreateFilterStore = (
  initialState: FilterState,
  onFilterSelected: (state: FilterState) => void
): FilterStore => {
  const selectFilterRef = useRef(onFilterSelected);
  selectFilterRef.current = onFilterSelected;
  return useRefFrom(() => createFilterStore(initialState, selectFilterRef))
    .current;
};
